import { createContext, useContext, useId } from 'react';
import type { ReactNode } from 'react';

import { WarningCircleIcon } from '../Icon';

export type ChoiceFeedbackProps = {
  /** Validation state, following Rating and NumberScale rather than text-field error/helperText. */
  invalid?: boolean;
  /** Shown and announced only while invalid. Supply translated recovery copy. */
  errorMessage?: string;
};

export const ChoiceGroupContext = createContext<{ invalid?: boolean; describedBy?: string }>({});

export const useChoiceFeedback = ({
  invalid,
  errorMessage,
  describedBy,
  inherit = true,
}: ChoiceFeedbackProps & { describedBy?: string; inherit?: boolean }) => {
  const group = useContext(ChoiceGroupContext);
  const errorId = useId();
  const effectiveInvalid = invalid ?? (inherit ? group.invalid : false) ?? false;
  const showError = effectiveInvalid && !!errorMessage;
  return {
    invalid: effectiveInvalid,
    errorId,
    showError,
    describedBy:
      [describedBy, inherit ? group.describedBy : undefined, showError ? errorId : undefined]
        .filter(Boolean)
        .join(' ') || undefined,
  };
};

export const ChoiceFeedback = ({ id, children }: { id: string; children: ReactNode }) => (
  <p
    id={id}
    role="alert"
    className="m-0 flex items-start gap-1.5 font-sans text-body-sm text-[color:var(--foreground-destructive)]"
  >
    <WarningCircleIcon aria-hidden="true" className="shrink-0" size={16} />
    <span>{children}</span>
  </p>
);
